import { useCallback, useEffect, useRef, useState } from "react";
import { CACHE_KEYS, CACHE_OPTIONS } from "../constants/cache-keys";
import { useAccount } from "../context";
import {
  AIAuditLog,
  AIControlResponse,
  AIControlState,
  AIStateUpdate,
  ApiResponse,
  ISuccessResponse,
} from "../interfaces/all";
import { apiService } from "../services/axios";
import { CacheManager } from "../utils/cache-manager";

const API_URL = `${import.meta.env.VITE_BACKEND_URL}/api`;
const MAX_LOGS = 100;

const isValidPhoneNumber = (phone: string) => {
  const cleanPhone = phone.replace(/\D/g, "");
  return cleanPhone.length >= 10;
};

const formatAuditLog = (log: AIAuditLog): AIAuditLog => ({
  ...log,
  timestamp: new Date(log.timestamp),
  reason: log.reason || undefined,
  user: log.user
    ? {
        id: log.user.id,
        name: log.user.name,
      }
    : undefined,
});

const getAuditLogs = (): AIAuditLog[] => {
  const logs = CacheManager.get<AIAuditLog[]>(
    CACHE_KEYS.AI.AUDIT_LOGS,
    CACHE_OPTIONS.AI.AUDIT_LOGS,
  );
  return (logs || []).map(formatAuditLog);
};

const saveAuditLog = (log: AIAuditLog) => {
  const logs = getAuditLogs();
  logs.unshift(formatAuditLog(log));
  // Limitar tamanho para evitar vazamento de memória
  const limitedLogs = logs.slice(0, MAX_LOGS);
  CacheManager.set(
    CACHE_KEYS.AI.AUDIT_LOGS,
    limitedLogs,
    CACHE_OPTIONS.AI.AUDIT_LOGS,
  );
};

const cleanupAuditLogs = () => {
  const logs = getAuditLogs();
  CacheManager.set(CACHE_KEYS.AI.AUDIT_LOGS, logs, CACHE_OPTIONS.AI.AUDIT_LOGS);
  return logs.length;
};

export const useAIControl = (phoneNumber: string) => {
  const [aiState, setAIState] = useState<AIControlState>({
    isActive: true,
    isUpdating: false,
    phoneNumber,
    updatedAt: new Date(),
  });
  const { socket, account } = useAccount();
  const cleanupRef = useRef<(() => void) | null>(null);

  // Verificar se a IA geral da empresa está ativada
  const isCompanyAIEnabled = account?.company?.meta?.aiAgent?.enabled ?? false;

  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, []);

  const cleanup = useCallback(() => {
    if (socket?.current) {
      socket.current.off("aiStateUpdate");
    }
  }, [socket]);

  useEffect(() => {
    cleanupRef.current = cleanup;
  }, [cleanup]);

  const fetchAIState = useCallback(async () => {
    if (!isValidPhoneNumber(phoneNumber) || !API_URL) {
      return;
    }

    try {
      const response = await apiService.get<AIControlResponse["data"]>(
        `/messages/ai/${phoneNumber}/state`,
      );
      if (response.status === "success") {
        const { data } = response;
        setAIState((prev: AIControlState) => ({
          ...prev,
          isActive: data?.isAIActive ?? prev.isActive,
          lastHumanInteraction: data?.lastHumanInteraction
            ? new Date(data.lastHumanInteraction)
            : prev.lastHumanInteraction,
          lastAIInteraction: data?.lastAIInteraction
            ? new Date(data.lastAIInteraction)
            : prev.lastAIInteraction,
          disabledReason: data?.disabledReason ?? prev.disabledReason,
          updatedAt: data?.updatedAt
            ? new Date(data.updatedAt)
            : prev.updatedAt,
        }));
      } else {
        console.error("Erro ao buscar estado da IA:", response);
      }
    } catch (error) {
      if (isValidPhoneNumber(phoneNumber)) {
        console.error("Erro ao buscar estado da IA:", error);
      }
    }
  }, [phoneNumber]);

  const toggleAI = async (
    isActive: boolean,
    reason?: string,
  ): Promise<ApiResponse<AIControlResponse["data"]> | undefined> => {
    if (!isValidPhoneNumber(phoneNumber) || !account) {
      return undefined;
    }

    // Se a IA geral da empresa estiver desativada, não permitir ativar para conversas individuais
    if (isActive && !isCompanyAIEnabled) {
      return {
        status: "error",
        message:
          "A IA geral da empresa está desativada. Ative primeiro nas configurações da empresa.",
      } as ApiResponse<AIControlResponse["data"]>;
    }

    const previousState = aiState.isActive;
    setAIState((prev: AIControlState) => ({ ...prev, isUpdating: true }));
    try {
      const response = await apiService.post<AIControlResponse>(
        `/messages/ai/${phoneNumber}/toggle`,
        {
          isActive,
          reason,
        },
      );

      if (response.status === "success") {
        const auditLog: AIAuditLog = {
          id: `${phoneNumber}-${Date.now()}`,
          phoneNumber,
          action: isActive ? "activate" : "deactivate",
          previousState,
          newState: response?.data?.data?.isAIActive ?? previousState,
          reason,
          timestamp: new Date(),
          user: {
            id: account.id,
            name: account.name,
          },
        };
        saveAuditLog(auditLog);

        setAIState((prev: AIControlState) => ({
          ...prev,
          isActive: response?.data?.data?.isAIActive ?? prev.isActive,
          lastHumanInteraction: response?.data?.data?.lastHumanInteraction
            ? new Date(response.data.data.lastHumanInteraction)
            : prev.lastHumanInteraction,
          lastAIInteraction: response?.data?.data?.lastAIInteraction
            ? new Date(response.data.data.lastAIInteraction)
            : prev.lastAIInteraction,
          disabledReason:
            response?.data?.data?.disabledReason ?? prev.disabledReason,
          updatedAt: response?.data?.data?.updatedAt
            ? new Date(response.data.data.updatedAt)
            : prev.updatedAt,
          isUpdating: false,
        }));

        return {
          status: "success",
          data: response?.data?.data ?? {},
        } as ISuccessResponse<AIControlResponse["data"]>;
      }

      return response;
    } catch (error) {
      console.error("Erro ao alterar estado da IA:", error);
      setAIState((prev: AIControlState) => ({ ...prev, isUpdating: false }));
      return error as ApiResponse<AIControlResponse["data"]>;
    }
  };

  useEffect(() => {
    if (!socket?.current || !phoneNumber) return;

    const handleStateUpdate = (update: AIStateUpdate) => {
      if (update.phoneNumber === phoneNumber) {
        setAIState((prev: AIControlState) => {
          if (
            prev.isActive === update.isAIActive &&
            prev.updatedAt?.getTime() === new Date(update.timestamp).getTime()
          ) {
            return prev;
          }

          const auditLog: AIAuditLog = {
            id: `${phoneNumber}-${Date.now()}`,
            phoneNumber,
            action: update.isAIActive ? "activate" : "deactivate",
            previousState: update.previousState,
            newState: update.isAIActive,
            reason: update.reason,
            timestamp: new Date(update.timestamp),
          };
          saveAuditLog(auditLog);

          return {
            ...prev,
            isActive: update?.isAIActive ?? prev.isActive,
            updatedAt: update?.timestamp
              ? new Date(update.timestamp)
              : prev.updatedAt,
          };
        });
      }
    };

    socket.current.on("aiStateUpdate", handleStateUpdate);

    return () => {
      socket.current?.off("aiStateUpdate", handleStateUpdate);
    };
  }, [socket, phoneNumber]);

  useEffect(() => {
    if (socket?.current?.connected && isValidPhoneNumber(phoneNumber)) {
      socket.current.emit("subscribe", { phoneNumber });
    }
  }, [socket, phoneNumber]);

  useEffect(() => {
    if (phoneNumber && isValidPhoneNumber(phoneNumber)) {
      // Chamar fetchAIState diretamente sem incluí-lo nas dependências
      const fetchState = async () => {
        if (!isValidPhoneNumber(phoneNumber) || !API_URL) {
          return;
        }

        try {
          const response = await apiService.get<AIControlResponse["data"]>(
            `/messages/ai/${phoneNumber}/state`,
          );
          if (response.status === "success") {
            const { data } = response;
            setAIState((prev: AIControlState) => ({
              ...prev,
              isActive: data?.isAIActive ?? prev.isActive,
              lastHumanInteraction: data?.lastHumanInteraction
                ? new Date(data.lastHumanInteraction)
                : prev.lastHumanInteraction,
              lastAIInteraction: data?.lastAIInteraction
                ? new Date(data.lastAIInteraction)
                : prev.lastAIInteraction,
              disabledReason: data?.disabledReason ?? prev.disabledReason,
              updatedAt: data?.updatedAt
                ? new Date(data.updatedAt)
                : prev.updatedAt,
            }));
          } else {
            console.error("Erro ao buscar estado da IA:", response);
          }
        } catch (error) {
          if (isValidPhoneNumber(phoneNumber)) {
            console.error("Erro ao buscar estado da IA:", error);
          }
        }
      };

      fetchState();
    }
  }, [phoneNumber]); // Removido fetchAIState das dependências

  useEffect(() => {
    const cleanupInterval = setInterval(
      () => {
        cleanupAuditLogs();
      },
      24 * 60 * 60 * 1000,
    );

    return () => {
      clearInterval(cleanupInterval);
    };
  }, []);

  // Estado efetivo da IA (considerando tanto o estado da conversa quanto o estado geral da empresa)
  const effectiveAIState = {
    ...aiState,
    isActive: aiState.isActive && isCompanyAIEnabled,
  };

  return {
    aiState: effectiveAIState,
    toggleAI,
    refreshState: fetchAIState,
    getAuditLogs,
    cleanupAuditLogs,
    isCompanyAIEnabled,
  };
};
